import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/primitives/Button';
import { Checkbox } from '@/components/primitives/Checkbox';
import { Input } from '@/components/primitives/Input';
import { OneTimeCodeInput } from '@/components/primitives/OneTimeCodeInput';
import { useTranslation } from '@/hooks/useTranslation';
import { COOKIE_POLICY_URL, PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from '@/lib/legal';
import { linkHitAreaClasses } from '@/lib/shared-variants';
import { cn } from '@/lib/utils';
import { captureAttribution } from '@/modules/auth/services/attribution';
import { signupApi } from '@/modules/auth/services/signupApi';
import { enterPlatform, hasSession } from '@/modules/auth/services/signupLanding';
import { loadWafSdk, WafCaptchaCancelled, wafResponse } from '@/modules/auth/services/waf';
import { responseErrors } from '@/modules/settings/utils/responseErrors';

import { AUTH_LINK_CLASS, AUTH_TITLE_CLASS, AuthLayout } from '../components/AuthLayout';
import { PasswordField } from '../components/PasswordField';
import { useAuthInputSize } from '../hooks/useAuthInputSize';
import { useCountdown } from '../hooks/useCountdown';

const POLL_INTERVAL_MS = 3000;
const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 120;
// Like the backend's check, the domain needs a dot: the browser's own lets `name@company` through.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// The consent copy runs to several lines, so the box sits beside its first line, not its middle:
// nudged down by half the line height left over around it.
const CONSENT_ROW_CLASS = 'items-start [&>span:first-child]:mt-xxs';
const CONSENT_TEXT_CLASS = 'text-size-n1 leading-[22px] text-shades-secondary';

/** What to say when the WAF, rather than the API, turned a request away. */
function wafError(error: unknown, t: (key: string) => string): string | undefined {
  if (error instanceof WafCaptchaCancelled) return t('Complete the security check to continue.');
  return wafResponse(error)
    ? t('We could not verify this browser. Reload the page and try again.')
    : undefined;
}

export function SignupPage() {
  const { t } = useTranslation();
  const signupId = useId();
  const inputSize = useAuthInputSize();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [sent, setSent] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [code, setCode] = useState('');
  const [cooldown, startCooldown] = useCountdown();

  // Captured on arrival: the campaign parameters are gone by the time the account exists.
  const [attribution] = useState(captureAttribution);

  // On mount, so the WAF's challenge and interaction signals are in place by the time of submit.
  useEffect(() => {
    void loadWafSdk();
  }, []);

  // The link can be opened anywhere; polling is what carries the session back to this tab.
  const status = useQuery({
    queryKey: ['signup-status', signupId, attempt],
    queryFn: () => signupApi.status(),
    enabled: sent,
    refetchInterval: (query) =>
      !query.state.data || query.state.data.state === 'pending' ? POLL_INTERVAL_MS : false,
    retry: false,
  });

  const claimMutation = useMutation({
    mutationFn: () => signupApi.claim(),
    onSuccess: (claimed) => {
      if (claimed.state === 'promoted') enterPlatform(claimed);
    },
  });
  // Both a code without a session and the poll lead here; claim once.
  const claim = () => {
    if (!claimMutation.isPending && !claimMutation.isSuccess) claimMutation.mutate();
  };

  const codeMutation = useMutation({
    mutationFn: (submittedCode: string) => signupApi.verifyCode(submittedCode),
    onSuccess: (verified) => {
      // Without a session, the link had already made the account.
      if (hasSession(verified)) enterPlatform(verified);
      else claim();
    },
  });

  const resetVerification = () => {
    setCode('');
    codeMutation.reset();
    claimMutation.reset();
  };

  const signupMutation = useMutation({
    mutationFn: () =>
      signupApi.signup({
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password,
        // The privacy policy box gates the button and covers the cookie policy too; marketing is
        // opt-in. The server records each with the signup and picks the country from the IP.
        accept_terms: privacyAccepted,
        cookie_consent: privacyAccepted,
        marketing_consent: marketingConsent,
        attribution,
      }),
    onSuccess: () => {
      setAttempt((current) => current + 1);
      setSent(true);
      startCooldown(RESEND_COOLDOWN_SECONDS);
    },
  });
  const resendMutation = useMutation({
    mutationFn: () => signupApi.resend(),
    onSuccess: () => {
      resetVerification();
      setAttempt((current) => current + 1);
      startCooldown(RESEND_COOLDOWN_SECONDS);
    },
  });

  const promoted = sent && status.data?.state === 'promoted';
  useEffect(() => {
    if (promoted) claim();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `claim` guards itself
  }, [promoted]);

  const emailMalformed = email.trim().length > 0 && !EMAIL_PATTERN.test(email.trim());

  const errors = responseErrors(signupMutation.error);
  const formError =
    errors.accept_terms ||
    errors.non_field_errors ||
    errors.detail ||
    wafError(signupMutation.error, t);
  const codeErrors = responseErrors(codeMutation.error);
  const resendErrors = responseErrors(resendMutation.error);

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    email.trim().length > 0 &&
    !emailMalformed &&
    passwordValid &&
    privacyAccepted &&
    !signupMutation.isPending;

  if (sent) {
    const expired = status.data?.state === 'expired';
    const busy = codeMutation.isPending || claimMutation.isPending || resendMutation.isPending;
    const codeComplete = code.length === CODE_LENGTH && /^\d+$/.test(code);
    const codeError = codeErrors.code || codeErrors.detail;
    const sentTo = t('Enter the six-digit code we sent to {email}.').split('{email}');
    return (
      <AuthLayout>
        <div className="flex flex-col gap-lg xl:w-[520px] xl:gap-10">
          <form
            className="flex flex-col gap-lg xl:gap-10"
            onSubmit={(event) => {
              event.preventDefault();
              if (codeComplete && !busy) codeMutation.mutate(code);
            }}
          >
            <div className="flex flex-col gap-lg">
              <div className="flex flex-col gap-sm">
                {/* Phones only: the desktop design has no heading here. */}
                <h1 className={cn(AUTH_TITLE_CLASS, 'xl:sr-only')}>{t('Verify your email')}</h1>
                <p className="text-size-n1 leading-[22px] text-shades-coal xl:text-foreground">
                  {sentTo[0]}
                  <span className="break-all text-brand">{email.trim()}</span>
                  {sentTo[1]}
                </p>
              </div>
              <OneTimeCodeInput
                focusOnMount
                variant="grouped"
                label={t('Verification code')}
                hideLabel
                length={CODE_LENGTH}
                value={code}
                onChange={setCode}
                onComplete={(completedCode) => codeMutation.mutate(completedCode)}
                disabled={busy}
                error={expired ? t('This code has expired. Send yourself a new one.') : codeError}
                className="py-sm xl:py-0"
              />
            </div>
            <Button
              type="submit"
              appearance="solid"
              kind="inverse"
              scale="m"
              width="full"
              loading={codeMutation.isPending}
              disabled={!codeComplete || busy}
            >
              {t('Verify and continue')}
            </Button>
          </form>

          {claimMutation.isError && (
            <p role="alert" className="text-center text-sm text-status-danger">
              {t('Your email is confirmed, but signing you in failed.')}{' '}
              <Link to="/login" className={AUTH_LINK_CLASS}>
                {t('Sign in')}
              </Link>
            </p>
          )}

          <div className="flex flex-col gap-xs text-center text-size-n1 leading-[22px]">
            <div className="flex flex-col items-center justify-center gap-sm">
              {cooldown > 0 ? (
                <p className="text-size-n2 leading-[18px] text-shades-secondary xl:text-size-n1 xl:leading-[22px] xl:text-foreground">
                  {t('Resend code in {time}', { time: formatCountdown(cooldown) })}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => resendMutation.mutate()}
                  disabled={busy}
                  className={AUTH_LINK_CLASS}
                >
                  {t("Didn't get it? Send a new code")}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  resetVerification();
                  resendMutation.reset();
                }}
                disabled={busy}
                className={AUTH_LINK_CLASS}
              >
                {t('Use a different email')}
              </button>
            </div>
            {resendMutation.isError && (
              <p role="alert" className="text-status-danger">
                {resendErrors.non_field_errors ||
                  resendErrors.detail ||
                  wafError(resendMutation.error, t) ||
                  t('Could not send another code. Try again shortly.')}
              </p>
            )}
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Split on every placeholder, so a translation is free to put the links in any order.
  const legalLinks: Record<string, { href: string; label: string }> = {
    '{termsOfUse}': { href: TERMS_AND_CONDITIONS_URL, label: t('Terms and Conditions') },
    '{privacyPolicy}': { href: PRIVACY_POLICY_URL, label: t('Privacy Policy') },
    '{cookiePolicy}': { href: COOKIE_POLICY_URL, label: t('Cookie Policy') },
  };
  const privacyConsent = t(
    'I have read and accept the {termsOfUse}, {privacyPolicy} and {cookiePolicy}.'
  )
    .split(/(\{termsOfUse\}|\{privacyPolicy\}|\{cookiePolicy\})/)
    .map((part) => {
      const link = legalLinks[part];
      if (!link) return part;
      return (
        <a
          key={part}
          href={link.href}
          target="_blank"
          rel="noreferrer"
          className={cn('text-brand underline', linkHitAreaClasses)}
        >
          {link.label}
        </a>
      );
    });

  return (
    <AuthLayout desktopNote>
      <form
        className="flex flex-col gap-lg xl:w-[560px] xl:gap-xxl"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) signupMutation.mutate();
        }}
      >
        <h1 className={AUTH_TITLE_CLASS}>{t('Create your account')}</h1>
        <div className="flex flex-col gap-lg">
          {/* Side by side from tablet width; stacked on phones, where two columns crowd the names. */}
          <div className="grid grid-cols-1 gap-lg sm:grid-cols-2">
            <Input
              size={inputSize}
              label={t('First name')}
              required
              autoComplete="given-name"
              value={firstName}
              onChange={(event) => {
                setFirstName(event.target.value);
                if (signupMutation.isError) signupMutation.reset();
              }}
              error={errors.first_name}
            />
            <Input
              size={inputSize}
              label={t('Last name')}
              required
              autoComplete="family-name"
              value={lastName}
              onChange={(event) => {
                setLastName(event.target.value);
                if (signupMutation.isError) signupMutation.reset();
              }}
              error={errors.last_name}
            />
          </div>
          <Input
            size={inputSize}
            type="email"
            label={t('Work email')}
            required
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              // The server's errors were about what was sent, so an edit clears them. Only
              // errors: resetting a pending signup would re-enable the button mid-request.
              if (signupMutation.isError) signupMutation.reset();
            }}
            onBlur={() => setEmailTouched(true)}
            placeholder="you@company.com"
            error={
              emailTouched && emailMalformed ? t('Enter a valid email address.') : errors.email
            }
          />
          <PasswordField
            size={inputSize}
            value={password}
            onChange={(value) => {
              setPassword(value);
              if (signupMutation.isError) signupMutation.reset();
            }}
            email={email.trim()}
            firstName={firstName.trim()}
            lastName={lastName.trim()}
            error={errors.password}
            onValidityChange={setPasswordValid}
          />
        </div>
        {/* Above the actions everywhere: the button stays disabled until the first is ticked. */}
        <div className="flex flex-col gap-sm">
          <Checkbox
            required
            checked={privacyAccepted}
            onChange={(event) => setPrivacyAccepted(event.target.checked)}
            containerClassName={CONSENT_ROW_CLASS}
            label={<span className={CONSENT_TEXT_CLASS}>{privacyConsent}</span>}
          />
          <Checkbox
            checked={marketingConsent}
            onChange={(event) => setMarketingConsent(event.target.checked)}
            containerClassName={CONSENT_ROW_CLASS}
            label={
              <span className={CONSENT_TEXT_CLASS}>
                {t(
                  'I agree that Nabat may use my name and email address to send me marketing communication, promotions and updates. I can withdraw my consent at any time.'
                )}
              </span>
            }
          />
        </div>
        {formError && (
          <p role="alert" className="text-sm text-status-danger">
            {formError}
          </p>
        )}
        <div className="flex flex-col gap-sm">
          <Button
            type="submit"
            appearance="solid"
            kind="inverse"
            scale="m"
            width="full"
            loading={signupMutation.isPending}
            disabled={!canSubmit}
          >
            {t('Sign up')}
          </Button>
          <p className="text-center text-size-n1 leading-[22px] text-foreground">
            {t('Already have an account?')}{' '}
            <Link to="/login" className={AUTH_LINK_CLASS}>
              {t('Sign in')}
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
}

/** 42 -> "0:42", the way the resend countdown reads. */
function formatCountdown(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
